import { Editor, Path, Element, Text, Descendant, Transforms } from 'slate';

import { FilterOperator, PREFIX_OPERATOR_SUGGESTIONS } from './constants';
import { ComparisonExpression, InExpression, parse } from './syntax';
import { CustomEditor, CustomElement, CustomText, QueryElementType } from './types';

const caseOperator = (operator: string): FilterOperator | undefined => {
  return operator?.toUpperCase() as FilterOperator;
};

const wrapValue = (value: string) => `"${value}"`;

export const conditionallyQuoteWrap = (value: string) => {
  const trimmedValue = value.trim();
  const quotedValueRegex = /^".*"$/;
  const booleanLiteralRegex = /^(true|false)$/i;

  if (quotedValueRegex.test(value)) {
    return value;
  }

  if (booleanLiteralRegex.test(trimmedValue)) {
    return trimmedValue;
  }

  return wrapValue(value);
};

const getContextInputFromListExpression = (peggyParsed: any) => {
  const lastParsedValue = peggyParsed?.values?.[peggyParsed.values.length - 1];
  const lastValue = lastParsedValue?.complete ? '' : lastParsedValue?.value;

  const valueOfLastValue =
    lastValue && typeof lastValue === 'string' ? lastValue.trim() : lastValue;
  const contextInput = lastValue?.complete ? '' : (valueOfLastValue ?? '');

  return contextInput;
};

const isLogical = (part?: string) => !!part && /^(AND|OR)$/i.test(part?.trim());

const getLocationData = (peggyParsed: any) => {
  return {
    identifierLocation: peggyParsed?.identifierLocation,
    operatorLocation: peggyParsed?.opLocation,
    valueLocation: peggyParsed?.valueLocation ?? peggyParsed?.valuesLocation,
  };
};

function findComparisonExpressions(expr: any): any[] {
  if (!expr) return [];

  const results: any[] = [];

  // Check if current expression is a comparison expression
  if (expr.kind === 'comparison-expression') {
    results.push(expr);
  }

  // Check nested expressions based on kind
  switch (expr.kind) {
    case 'and-expression':
    case 'or-expression': {
      results.push(
        ...findComparisonExpressions(expr.left),
        ...findComparisonExpressions(expr.right),
      );

      break;
    }
    case 'implicit-and-expression': {
      results.push(
        ...findComparisonExpressions(expr.left),
        ...findComparisonExpressions(expr.right),
      );

      break;
    }
    case 'not-expression': {
      results.push(...findComparisonExpressions(expr.expression));

      break;
    }
    case 'parenthesized-expression': {
      results.push(...findComparisonExpressions(expr.expr));

      break;
    }
    // No default
  }

  return results;
}

export const parseFindComparisonExpressions = (
  text: string,
): { field: string; operator: FilterOperator; text: string; value: string }[] => {
  try {
    const peggyParsed = parse(text);
    const comparisonExpressions = findComparisonExpressions(peggyParsed);

    return comparisonExpressions.map((expr) => {
      return {
        field: expr.identifier.value,
        operator: caseOperator(expr.op) as FilterOperator,
        text: expr.text,
        value: expr.value?.text,
      };
    });
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('Error parsing findComparisonExpressions', error);
    }

    return [];
  }
};

export const parseExpression = (
  text: string,
): [
  // prefix-operator, field, operator, value
  [string?, string?, FilterOperator?, string?],
  {
    complete: boolean;
    context: 'prefix-operator' | 'field' | 'operator' | 'value';
    contextInput: string;
    identifierLocation?: { end: { offset: number }; start: { offset: number } };
    notLocation?: { end: { offset: number }; start: { offset: number } };
    operatorLocation?: { end: { offset: number }; start: { offset: number } };
    valueLocation?: { end: { offset: number } | number; start: { offset: number } | number };
  },
] => {
  const trimmedText = text.trimStart();

  if (!trimmedText) {
    return [
      [undefined, undefined, undefined, undefined],
      {
        complete: false,
        context: 'field',
        contextInput: '',
        identifierLocation: undefined,
        operatorLocation: undefined,
        valueLocation: undefined,
      },
    ];
  }

  try {
    const peggyParsed = parse(text);

    switch (peggyParsed?.kind) {
      case 'incomplete-expression': {
        return [
          [
            undefined,
            peggyParsed.identifier.value,
            peggyParsed.op ? caseOperator(peggyParsed.op) : undefined,
            undefined,
          ],
          {
            complete: false,
            context: peggyParsed.context,
            contextInput: '',
            ...getLocationData(peggyParsed),
          },
        ];
      }
      case 'not-expression': {
        const comparisonExpression = peggyParsed.expression as ComparisonExpression;
        const contextInput = getContextInputFromListExpression(comparisonExpression);

        return [
          [
            PREFIX_OPERATOR_SUGGESTIONS[0],
            comparisonExpression?.identifier?.value,
            caseOperator(comparisonExpression?.op),
            (comparisonExpression as unknown as InExpression)?.values
              ?.map((v) => v.text)
              .join(', ') ?? comparisonExpression?.value?.text,
          ],
          {
            complete: comparisonExpression?.complete ?? false,
            context: comparisonExpression?.context ?? 'prefix-operator',
            contextInput,
            ...getLocationData(comparisonExpression),
            notLocation: peggyParsed.opLocation,
          },
        ];
      }
      case 'contains-expression': {
        return [
          [
            undefined,
            peggyParsed.identifier.value,
            FilterOperator.contains,
            peggyParsed.value?.text,
          ],
          {
            complete: peggyParsed.complete,
            context: peggyParsed.context,
            contextInput: peggyParsed.value?.text ?? '',
            ...getLocationData(peggyParsed),
          },
        ];
      }
      case 'comparison-expression': {
        return [
          [
            undefined,
            peggyParsed.identifier.value,
            caseOperator(peggyParsed.op),
            peggyParsed.value?.text,
          ],
          {
            complete: peggyParsed.complete,
            context: peggyParsed.context,
            contextInput: peggyParsed.value?.text ?? '',
            ...getLocationData(peggyParsed),
          },
        ];
      }
      case 'void-expression': {
        return [
          [undefined, peggyParsed.identifier.value, caseOperator(peggyParsed.op), undefined],
          {
            complete: peggyParsed.complete,
            context: 'operator',
            contextInput: peggyParsed.op,
            ...getLocationData(peggyParsed),
          },
        ];
      }
      case 'list-expression': {
        const contextInput = getContextInputFromListExpression(peggyParsed);

        return [
          [
            undefined,
            peggyParsed.identifier.value,
            caseOperator(peggyParsed.op),
            peggyParsed.values?.map((v) => v.text).join(', '),
          ],
          {
            complete: peggyParsed.complete,
            context: 'value',
            contextInput,
            ...getLocationData(peggyParsed),
          },
        ];
      }
      case 'search-expression': {
        const split = trimmedText.split(' ');

        return [
          [undefined, split[0], split[1] ? (' ' as any) : undefined, split[1] as any],
          {
            complete: false,
            context: 'operator',
            contextInput: split[1] ?? '',
            ...getLocationData(peggyParsed),
          },
        ];
      }
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('Error parsing parseExpression', error);
    }
  }

  return [
    [undefined, trimmedText, undefined, undefined],
    {
      complete: false,
      context: 'field',
      contextInput: '',
      identifierLocation: undefined,
      operatorLocation: undefined,
      valueLocation: undefined,
    },
  ];
};

/**
 * Returns true if the filter string parses as a complete, valid filter expression.
 * Used to avoid syncing or triggering incomplete drafts
 */
export const isFilterComplete = (filterString: string): boolean => {
  const trimmed = filterString?.trim();
  if (!trimmed) {
    return true;
  }
  try {
    const result = parse(trimmed);

    return result?.complete === true;
  } catch {
    return false;
  }
};

const extractTextParts = (elements: (CustomElement | CustomText)[]): string[] => {
  const parts: string[] = [];
  for (const element of elements) {
    if ('text' in element) {
      const trimmedText = element.text.trim();
      if (trimmedText) {
        parts.push(trimmedText);
      }
    } else {
      switch (element.type) {
        case 'paragraph': {
          parts.push(...extractTextParts(element.children));
          break;
        }
        case 'simple-chip': {
          parts.push(element.value);
          break;
        }
        case 'chip': {
          const chipText = element.children[0]?.text?.trim();

          if (chipText) {
            parts.push(chipText);
          }
          break;
        }
        case 'logical-operator':
        case 'grouping-operator': {
          parts.push(element.operator);
          break;
        }
      }
    }
  }

  return parts;
};

export const serializeQueryElements = (elements: (CustomElement | CustomText)[]): string => {
  const textParts = extractTextParts(elements);

  const processedTextParts: string[] = [];

  for (const currentPart of textParts) {
    processedTextParts.push(currentPart);
  }
  if (processedTextParts.length > 0 && isLogical(processedTextParts[0])) {
    processedTextParts.shift();
  }
  if (processedTextParts.length > 0 && isLogical(processedTextParts.at(-1))) {
    processedTextParts.pop();
  }

  const rawString = processedTextParts.join(' ');

  return rawString
    .replaceAll(/\b(AND|OR)\b/gi, ' $1 ')
    .replaceAll(/\(\s+/g, '(')
    .replaceAll(/\s+\)/g, ')')
    .replaceAll(/\s+/g, ' ')
    .trim();
};

export const getTypeOfPreviousElement = (
  editor: CustomEditor,
  path: Path,
): QueryElementType | null => {
  let currentPath = path;
  const looping = true;

  while (looping) {
    try {
      const previousNodeEntry = Editor.previous(editor, { at: currentPath });

      if (!previousNodeEntry) {
        return null;
      }

      const [previousNode, previousPath] = previousNodeEntry;
      currentPath = previousPath;

      if (Element.isElement(previousNode)) {
        if (previousNode.type === 'grouping-operator') {
          return previousNode.operator === '('
            ? 'grouping-operator-opener'
            : 'grouping-operator-closer';
        }

        return previousNode.type as QueryElementType;
      }

      if (Text.isText(previousNode) && previousNode.text.trim() !== '') {
        return 'text' as QueryElementType;
      }
    } catch {
      return null;
    }
  }

  return null;
};

// Recursively count or collect all chip nodes at any level of the document
export const findAllChipNodes = (
  nodes: Descendant[],
  collect: boolean = false,
): number | Element[] => {
  let count = 0;
  const collected: Element[] = collect ? [] : [];

  const traverse = (node: Node) => {
    // Check if current node is a chip
    if (Element.isElement(node) && (node.type === 'chip' || node.type === 'simple-chip')) {
      if (collect) {
        collected.push(node);
      } else {
        count++;
      }
    }

    // Recursively check children if they exist
    if (Element.isElement(node) && Array.isArray(node.children)) {
      for (const child of node.children) {
        traverse(child as unknown as Node);
      }
    }
  };

  // Process each top-level node
  for (const node of nodes) {
    traverse(node as unknown as Node);
  }

  return collect ? collected : count;
};

/**
 * Utility function to initialize the editor state by simulating
 * character-by-character input of a string.
 *
 * This approach mimics user input but can be computationally
 * expensive for long strings due to repeated normalization cycles.
 *
 * @param editor The CustomEditor instance (with plugins applied).
 * @param initialValue The string to initialize the editor with.
 * @returns The resulting editor children (Descendant[]).
 */
// [PURPOSE]: Initializes the Slate editor content by simulating the typing of an initial string character by character, forcing normalization after each character. This ensures that text patterns are correctly converted to structured elements (like chips) as if a user had typed them. Returns the final editor state.
export const initializeEditorFromString = (
  editor: CustomEditor,
  initialValue: string,
): Descendant[] => {
  // 1. Set editor to a known, valid, empty state
  editor.children = [{ children: [{ text: '' }], type: 'paragraph' }];

  // 2. Select the start of that known state (path [0, 0] is the text node)
  Transforms.select(editor, Editor.start(editor, [0, 0]));

  // 3. Simulate typing character by character
  for (const char of initialValue) {
    Transforms.insertText(editor, char);
    // Force normalization after each character to trigger conversions
    Editor.normalize(editor, { force: true });
  }

  // Return the final state
  // Use structuredClone if available, otherwise fallback to JSON parse/stringify
  try {
    return structuredClone(editor.children);
  } catch {
    return JSON.parse(JSON.stringify(editor.children)) as Descendant[];
  }
};

export const restoreEditorFromSnapshot = (
  editor: CustomEditor,
  snapshot: Descendant[],
): Descendant[] => {
  const restored = structuredClone(snapshot);
  editor.children = restored;
  editor.selection = null;
  try {
    Transforms.select(editor, Editor.end(editor, []));
  } catch {
    // Selection may fail on empty content; safe to ignore
  }

  return restored;
};
